import React, { useState, useEffect } from 'react';
import {
    Button,
    Card,
    CardBody,
    FormControl,
    FormLabel,
    Grid,
    Input,
    Select,
    HStack,
    Menu,
    MenuButton,
    MenuList,
    MenuItemOption,
    MenuOptionGroup,
    Text,
    useToast,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { FiSearch, FiDownload, FiColumns } from 'react-icons/fi';
import dayjs from 'dayjs';
import api from '../../../auth/apiClient';
import { WeighbridgeFilters } from '../types';

interface Company {
    id: number;
    name: string;
}

interface Estate {
    id: number;
    name: string;
    odoo_id?: number;
    company_id?: number;
}

interface FilterWeighbridgeProps {
    filters: WeighbridgeFilters;
    onApply: (filters: WeighbridgeFilters) => void;
    onExportExcel: () => void;
    onToggleColumns: () => void;
    isAdmin?: boolean;
    isLoading?: boolean;
    isExporting?: boolean;
    hasData?: boolean;
}

export const FilterWeighbridge: React.FC<FilterWeighbridgeProps> = ({
    filters,
    onApply,
    onExportExcel,
    onToggleColumns,
    isAdmin = false,
    isLoading = false,
    isExporting = false,
    hasData = false,
}) => {
    const [dateFrom, setDateFrom] = useState(filters.date_from);
    const [dateTo, setDateTo] = useState(filters.date_to);
    const [companyId, setCompanyId] = useState<number | undefined>(filters.company_id ?? 1);
    const [selectedEstateIds, setSelectedEstateIds] = useState<number[]>(filters.estate_ids || []);

    const [companies, setCompanies] = useState<Company[]>([]);
    const [estates, setEstates] = useState<Estate[]>([]);
    const toast = useToast();

    useEffect(() => {
        const fetchDropdowns = async () => {
            try {
                const [optionsRes, estatesDbRes] = await Promise.all([
                    api.get('/inspeksi/options'),
                    api.get('/inspeksi/web/estate-db').catch(() => null),
                ]);

                if (optionsRes.data) {
                    setCompanies(optionsRes.data.companies || []);
                }

                // Prioritaskan /inspeksi/web/estate-db karena memiliki company_id yang lengkap per estate
                if (estatesDbRes && estatesDbRes.data && Array.isArray(estatesDbRes.data)) {
                    setEstates(estatesDbRes.data);
                } else if (optionsRes.data && optionsRes.data.estates) {
                    setEstates(optionsRes.data.estates);
                }
            } catch (err) {
                console.error('Failed to load filter options:', err);
            }
        };
        fetchDropdowns();
    }, []);

    // Filter estates by selected company if chosen
    const availableEstates = companyId
        ? estates.filter((e) => e.company_id === companyId)
        : estates;

    // Helper inisial singkatan estate (misal: MUSI MAKMUR -> MME, LEMATANG MAKMUR -> LME, PLASMA ABAB MAKMUR 1 -> PAM1)
    const getEstateAbbreviation = (name: string): string => {
        const upper = name.toUpperCase().trim();
        if (upper.includes('MUSI MAKMUR') || upper === 'ESTATE MUSI MAKMUR') return 'MME';
        if (upper.includes('LEMATANG MAKMUR') || upper === 'ESTATE LEMATANG MAKMUR') return 'LME';
        if (upper.includes('ABAB MAKMUR 1') || upper.includes('ABAB 1') || upper.includes('PLASMA ABAB 1')) return 'PAM1';
        if (upper.includes('ABAB MAKMUR 2') || upper.includes('ABAB 2') || upper.includes('PLASMA ABAB 2')) return 'PAM2';
        if (upper.includes('PENUKAL')) return 'PN';
        if (upper.includes('MAJU BERSAMA')) return 'MB';
        if (upper.includes('JAZA CHINDO')) return 'JC';
        if (upper.includes('KARANG SEJAHTERA')) return 'KS';
        if (upper.includes('BINA TANI')) return 'BT';
        if (upper.includes('ABADI BERSAMA')) return 'AB';
        if (upper.includes('HARAPAN MAKMUR')) return 'HM';
        if (upper === 'MRN') return 'MRN';

        // Fallback cerdas: jika ada kata "ESTATE", ambil inisial kata berikutnya + 'E'
        if (upper.startsWith('ESTATE ')) {
            const words = upper.replace('ESTATE ', '').split(/\s+/);
            const initials = words.map((w) => w[0]).join('');
            return `${initials}E`;
        }

        return name;
    };

    // Helper untuk menampilkan ringkasan nama estate yang dipilih di tombol dropdown
    const getSelectedEstateLabel = () => {
        if (selectedEstateIds.length === 0) return 'Semua Estate';

        const selectedAbbrs = selectedEstateIds
            .map((id) => {
                const found = availableEstates.find((e) => (e.odoo_id || e.id) === id);
                return found ? getEstateAbbreviation(found.name) : null;
            })
            .filter(Boolean) as string[];

        if (selectedAbbrs.length === 0) {
            return `${selectedEstateIds.length} Estate dipilih`;
        }

        // Tampilkan semua singkatan yang dipilih dipisah koma
        return selectedAbbrs.join(', ');
    };

    const handleApply = () => {
        const start = dayjs(dateFrom);
        const end = dayjs(dateTo);

        if (end.isBefore(start)) {
            toast({
                title: 'Rentang Tanggal Tidak Valid',
                description: 'Tanggal akhir posting tidak boleh lebih awal dari tanggal awal.',
                status: 'warning',
                duration: 4000,
                isClosable: true,
                position: 'top-right',
            });
            return;
        }

        const diffDays = end.diff(start, 'day') + 1;
        if (diffDays > 31) {
            toast({
                title: 'Rentang Tanggal Terlalu Panjang',
                description: `Rentang tanggal maksimal adalah 31 hari.`,
                status: 'warning',
                duration: 5000,
                isClosable: true,
                position: 'top-right',
            });
            return;
        }

        onApply({
            date_from: dateFrom,
            date_to: dateTo,
            company_id: companyId,
            estate_ids: selectedEstateIds,
        });
    };

    return (
        <Card bg="white" shadow="sm" borderRadius="lg">
            <CardBody p={4}>
                <Grid
                    templateColumns={{
                        base: '1fr',
                        md: 'repeat(2, 1fr)',
                        lg: 'repeat(4, 1fr) auto',
                    }}
                    gap={4}
                    alignItems="end"
                >
                    <FormControl size="sm">
                        <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                            Tanggal Posting Dari
                        </FormLabel>
                        <Input
                            type="date"
                            size="sm"
                            borderRadius="md"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                        />
                    </FormControl>

                    <FormControl size="sm">
                        <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                            Tanggal Posting Sampai
                        </FormLabel>
                        <Input
                            type="date"
                            size="sm"
                            borderRadius="md"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                        />
                    </FormControl>

                    <FormControl size="sm">
                        <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                            Company
                        </FormLabel>
                        <Select
                            size="sm"
                            borderRadius="md"
                            placeholder="Semua Company"
                            value={companyId || ''}
                            onChange={(e) => {
                                const val = e.target.value ? Number(e.target.value) : undefined;
                                setCompanyId(val);
                                setSelectedEstateIds([]); // reset estate when company changes
                            }}
                        >
                            {companies.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}
                                </option>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="sm">
                        <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                            Estate
                        </FormLabel>
                        <Menu closeOnSelect={false}>
                            <MenuButton
                                as={Button}
                                size="sm"
                                variant="outline"
                                width="100%"
                                textAlign="left"
                                rightIcon={<ChevronDownIcon />}
                                fontWeight="normal"
                            >
                                <Text isTruncated fontSize="xs">
                                    {getSelectedEstateLabel()}
                                </Text>
                            </MenuButton>
                            <MenuList maxH="250px" overflowY="auto" zIndex={20}>
                                <MenuOptionGroup
                                    type="checkbox"
                                    value={selectedEstateIds.map(String)}
                                    onChange={(vals) => {
                                        const ids = (vals as string[]).map(Number);
                                        setSelectedEstateIds(ids);
                                    }}
                                >
                                    {availableEstates.map((est) => {
                                        const estateValue = est.odoo_id || est.id;
                                        return (
                                            <MenuItemOption
                                                key={`${est.id}-${estateValue}`}
                                                value={String(estateValue)}
                                                fontSize="xs"
                                            >
                                                {est.name}
                                            </MenuItemOption>
                                        );
                                    })}
                                </MenuOptionGroup>
                            </MenuList>
                        </Menu>
                    </FormControl>

                    <HStack spacing={2} pt={{ base: 2, lg: 0 }}>
                        <Button
                            size="sm"
                            colorScheme="blue"
                            leftIcon={<FiSearch />}
                            onClick={handleApply}
                            isLoading={isLoading}
                        >
                            Filter
                        </Button>
                        {isAdmin && (
                            <Button
                                size="sm"
                                variant="outline"
                                leftIcon={<FiColumns />}
                                onClick={onToggleColumns}
                            >
                                Kolom
                            </Button>
                        )}
                        <Button
                            size="sm"
                            colorScheme="green"
                            variant="outline"
                            leftIcon={<FiDownload />}
                            onClick={onExportExcel}
                            isLoading={isExporting}
                            loadingText="Mendownload..."
                            isDisabled={!hasData || isLoading || isExporting}
                        >
                            Excel
                        </Button>
                    </HStack>
                </Grid>
            </CardBody>
        </Card>
    );
};
